import { useState, useEffect } from "react";
import Layout from '@/components/Layout';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useTranslations } from "@/lib/translations";
import { usePageMeta } from "@/hooks/use-page-meta";
import { useJsonLd } from "@/hooks/use-json-ld";
import { buildBreadcrumbList } from "@/lib/breadcrumbs";
import { pushEvent } from "@/lib/analytics";
import CaptchaField from "@/components/CaptchaField";
import { isWorkEmail } from "@workspace/email-policy";
import { 
  Mail, 
  CheckCircle,
  Send
} from "lucide-react";

const Contact = () => {
  const { toast } = useToast();
  const { t, isRTL, language } = useTranslations();
  
  usePageMeta({
    title: "Request ESG Platform Access | ESG Invest",
    description:
      "Request access to ESG ratings for 880+ Middle East companies. Sign up for a trial of the ESG Invest platform — used by institutional investors and asset managers.",
    path: "/contact",
  });

  useJsonLd(
    "ld-contact-breadcrumbs",
    buildBreadcrumbList([
      { name: "Home", path: "/" },
      { name: "Contact", path: "/contact" },
    ]),
  );
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    jobTitle: '',
    investmentFocus: '',
    phone: '',
    message: '',
    website: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!isWorkEmail(formData.email)) {
      setSubmitError(t('lead.form.workEmailRequired'));
      return;
    }
    setSubmitting(true);
    pushEvent('form_contact_submit', {
      investment_focus: formData.investmentFocus || undefined,
      has_phone: Boolean(formData.phone),
    });
    try {
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: fullName,
          email: formData.email.trim(),
          company: formData.company.trim() || null,
          phone: formData.phone.trim() || null,
          message: formData.message.trim() || null,
          source: 'contact_page',
          locale: language,
          context: {
            jobTitle: formData.jobTitle || null,
            investmentFocus: formData.investmentFocus || null,
          },
          website: formData.website,
          captchaToken,
          captchaAnswer: captchaAnswer.trim(),
        }),
      });
      if (!res.ok) {
        let reason: string | undefined;
        try {
          const body = (await res.json()) as { reason?: string };
          reason = body.reason;
        } catch { /* ignore */ }
        if (reason && reason.startsWith('captcha')) {
          setCaptchaToken('');
          setCaptchaAnswer('');
          throw new Error(t('lead.form.captchaWrong'));
        }
        if (reason === 'email_not_work') {
          throw new Error(t('lead.form.workEmailRequired'));
        }
        throw new Error(t('lead.toast.errorDesc'));
      }
      if (typeof window !== 'undefined') {
        const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
        w.dataLayer = w.dataLayer ?? [];
        w.dataLayer.push({
          event: 'form_lead_submitted',
          source: 'contact_page',
          locale: language,
        });
      }
      toast({
        title: t('contact.toast.title'),
        description: t('contact.toast.desc'),
      });
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        jobTitle: '',
        investmentFocus: '',
        phone: '',
        message: '',
        website: '',
      });
      setCaptchaToken('');
      setCaptchaAnswer('');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('lead.toast.errorDesc'));
    } finally {
      setSubmitting(false);
    }
  };

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const isFormValid =
    formData.firstName.trim() !== '' &&
    formData.lastName.trim() !== '' &&
    isValidEmail(formData.email) &&
    formData.company.trim() !== '' &&
    formData.phone.trim() !== '' &&
    captchaToken.trim() !== '' &&
    captchaAnswer.trim() !== '';

  const contactInfo = [
    {
      icon: Mail,
      titleKey: 'contact.info.email',
      details: "info@esginvest.com",
      descKey: 'contact.getInTouch'
    },
  ];


  return (
    <Layout>
      <section className="relative text-white py-20 overflow-hidden" style={{ backgroundImage: 'url(/subscriptions-hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-black/60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
            <Mail className="h-4 w-4 text-accent" />
            {t('contact.badge')}
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            {t('contact.title')}
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      <div className="bg-se-gray-50 dark:bg-background py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            
            <div className="lg:col-span-2">
              <Card className="p-8 border-0 shadow-lg">
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-foreground mb-4">
                    {t('contact.requestDemo')}
                  </h2>
                  <p className="text-muted-foreground">
                    {t('contact.formDesc')}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">{t('contact.form.firstName')} *</Label>
                      <Input
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        required
                        className="border-input"
                        data-testid="input-first-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">{t('contact.form.lastName')} *</Label>
                      <Input
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        required
                        className="border-input"
                        data-testid="input-last-name"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{t('contact.form.email')} *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                      className="border-input"
                      data-testid="input-email"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company">{t('contact.form.company')} *</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        required
                        className="border-input"
                        data-testid="input-company"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="jobTitle">{t('contact.form.jobTitle')}</Label>
                      <Input
                        id="jobTitle"
                        value={formData.jobTitle}
                        onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                        className="border-input"
                        data-testid="input-job-title"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="investmentFocus">{t('contact.form.employees')}</Label>
                      <Select onValueChange={(value) => handleInputChange('investmentFocus', value)}>
                        <SelectTrigger className="border-input" data-testid="select-investment-focus">
                          <SelectValue placeholder={t('contact.form.selectSize')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="individual" data-testid="option-individual">{t('contact.form.size1')}</SelectItem>
                          <SelectItem value="asset-management" data-testid="option-asset-management">{t('contact.form.size2')}</SelectItem>
                          <SelectItem value="institutional" data-testid="option-institutional">{t('contact.form.size3')}</SelectItem>
                          <SelectItem value="advisor" data-testid="option-advisor">{t('contact.form.size4')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">{t('contact.info.phone')} *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        required
                        className="border-input"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t('contact.form.message')}</Label>
                    <Textarea
                      id="message"
                      placeholder={t('contact.form.messagePlaceholder')}
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      className="min-h-[120px] border-input"
                      data-testid="input-message"
                    />
                  </div>

                  <CaptchaField
                    token={captchaToken}
                    onTokenChange={setCaptchaToken}
                    answer={captchaAnswer}
                    onAnswerChange={setCaptchaAnswer}
                    disabled={submitting}
                  />

                  <div className="hidden" aria-hidden="true">
                    <label>
                      Website
                      <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={formData.website}
                        onChange={(e) => handleInputChange('website', e.target.value)}
                      />
                    </label>
                  </div>

                  {submitError && (
                    <p className="text-sm text-destructive" role="alert" data-testid="contact-error">
                      {submitError}
                    </p>
                  )}

                  <Button
                    type="submit"
                    variant="accent"
                    className="w-full text-lg py-6"
                    size="lg"
                    disabled={submitting || !isFormValid}
                    data-testid="button-submit-request"
                  >
                    <Send className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {submitting ? t('lead.form.submitting') : t('contact.form.submit')}
                  </Button>
                </form>
              </Card>
            </div>

            <div className="space-y-8">
              <Card className="p-6 border-0 shadow-lg">
                <h3 className="text-xl font-semibold text-foreground mb-6">
                  {t('contact.info.title')}
                </h3>
                <div className="space-y-6">
                  {contactInfo.map((info, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="bg-gradient-primary p-2 rounded-lg flex-shrink-0">
                        <info.icon className="h-4 w-4 text-white" />
                      </div>
                      <div className={isRTL ? 'text-right' : ''}>
                        <h4 className="font-medium text-foreground">{t(info.titleKey)}</h4>
                        {Array.isArray(info.details) ? (
                          <div className="text-sm font-medium text-se-green">
                            {info.details.map((line, i) => (
                              <p key={i}>{line}</p>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm font-medium text-se-green">{info.details}</p>
                        )}
                        <p className="text-xs text-muted-foreground">{t(info.descKey)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Contact;
